import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { Button } from "../ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = `${BACKEND_URL}/api/uploads/photos/elitethebarber-logo-transparent.png`;

const NAV_ITEMS = [
  { key: "home", to: "/" },
  { key: "services", to: "/diensten" },
  { key: "contact", to: "/contact" },
  { key: "manage", to: "/mijn-afspraak" },
];

const DesktopNav = ({ t }) => (
  <nav className="hidden lg:flex items-center gap-8">
    {NAV_ITEMS.map((item) => (
      <NavLink
        key={item.key}
        to={item.to}
        data-testid={`nav-${item.key}-link`}
        className={({ isActive }) =>
          `text-sm uppercase tracking-widest transition-colors ${
            isActive ? "text-gold" : "text-white/70 hover:text-white"
          }`
        }
      >
        {t(`nav.${item.key}`)}
      </NavLink>
    ))}
  </nav>
);

const MobileNavSheet = ({ open, onOpenChange, t }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetTrigger asChild>
      <button data-testid="mobile-menu-button" className="p-2 text-white">
        <Menu size={24} />
      </button>
    </SheetTrigger>
    <SheetContent side="right" className="bg-[#0A0A0A] border-white/10 w-[280px]">
      <div className="flex flex-col gap-6 mt-12">
        {NAV_ITEMS.map((item) => (
          <Link key={item.key} to={item.to} data-testid={`mobile-nav-${item.key}-link`} className="text-lg text-white/90 hover:text-gold">
            {t(`nav.${item.key}`)}
          </Link>
        ))}
        <Link to="/boeken" data-testid="mobile-book-link">
          <Button className="w-full bg-gold text-white hover:bg-gold-hover rounded-none">{t("header.bookNow")}</Button>
        </Link>
        <a href="tel:0618885582" className="flex items-center gap-2 text-white/70 mt-4">
          <Phone size={16} /> 06 18885582
        </a>
      </div>
    </SheetContent>
  </Sheet>
);

export const Header = () => {
  const { t, lang, toggleLang } = useLanguage();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b transition-colors duration-300 ${
        scrolled ? "bg-black/80 border-white/10" : "bg-black/40 border-white/5"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 h-24 lg:h-28">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          <img
            src={LOGO_URL}
            alt="EliteTheBarber logo"
            className="h-24 lg:h-28 w-auto drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]"
          />
        </Link>

        <DesktopNav t={t} />

        <div className="hidden lg:flex items-center gap-4">
          <button
            data-testid="lang-toggle-desktop"
            onClick={toggleLang}
            className="text-sm text-white/70 hover:text-gold transition-colors tracking-widest"
          >
            {lang === "nl" ? "NL" : "EN"} / {lang === "nl" ? "EN" : "NL"}
          </button>
          <Link to="/boeken">
            <Button data-testid="header-book-button" className="bg-gold text-white hover:bg-gold-hover rounded-none px-6 transition-colors">
              {t("header.bookNow")}
            </Button>
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <button data-testid="lang-toggle-mobile" onClick={toggleLang} className="text-sm text-white/80 tracking-widest px-2">
            {lang === "nl" ? "EN" : "NL"}
          </button>
          <MobileNavSheet open={open} onOpenChange={setOpen} t={t} />
        </div>
      </div>
    </header>
  );
};
